/**
 * StaticDailyRewardsChart — Print-/Export-Variante von DailyRewardsChart.
 *
 * WARUM: `DailyRewardsChart` rendert recharts mit `<ResponsiveContainer>`.
 * recharts haengt dabei ZWEI ResizeObserver an (ResponsiveContainer +
 * RechartsWrapper), beide mit `debounce: 0`, und beide schreiben die gemessene
 * Größe per `setState` zurück (node_modules/recharts/lib/component/
 * ResponsiveContainer.js:97-127 und lib/chart/RechartsWrapper.js:70-99).
 * Sobald `window.print()` das Layout auf die Seitenbreite umstellt, entsteht
 * daraus eine Mess-/Render-Rueckkopplung, die den Print-Reflow beliebig lange
 * blockiert.
 *
 * Diese Komponente rendert dieselbe Serie als statisches, in Pixeln fest
 * dimensioniertes SVG: kein ResizeObserver, kein State, kein Tooltip, keine
 * Animation. Damit ist das gedruckte Dokument layout-stabil.
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { DailyReward } from './useMonthlyReport'
import { buildFullMonthSeries } from './dailyRewardsUtils'
import { isPeriodInRewardDataGap } from '../../lib/rewardDataGap'
import { RewardDataGapNotice } from '../../components/RewardDataGapNotice'

import { buildStaticChartGeometry, VIEW_W, VIEW_H, PAD_L, PAD_R } from './staticChartGeometry'

const HNT_COLOR = '#22c55e'

type Props = {
  daily: DailyReward[]
  period?: string
}

export function StaticDailyRewardsChart({ daily, period }: Props) {
  const { t } = useTranslation('reports')
  const isEmpty = daily.length === 0
  const isDataGap = period ? isPeriodInRewardDataGap(period) : false

  const series = useMemo(() => {
    if (period) return buildFullMonthSeries(daily, period)
    return [...daily].sort((a, b) => a.date.localeCompare(b.date))
  }, [daily, period])

  const geometry = useMemo(() => buildStaticChartGeometry(series), [series])

  return (
    <div className="card card-pad report-static-chart" data-testid="report-static-chart">
      <div>
        <h3 className="section-heading" style={{ marginBottom: 0 }}>
          {t('chart.title')}
        </h3>
        <p className="muted" style={{ fontSize: 11, margin: '2px 0 0' }}>
          {t('chart.subtitle')}
        </p>
      </div>

      {isDataGap && <RewardDataGapNotice />}

      {isEmpty ? (
        <p className="muted" aria-label={t('chart.empty.ariaLabel')}>
          {t('chart.empty.message')}
        </p>
      ) : (
        <svg
          role="img"
          aria-label={t('chart.ariaLabel')}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          width="100%"
          height={VIEW_H}
          preserveAspectRatio="xMidYMid meet"
          style={{ marginTop: 8, display: 'block' }}
        >
          {geometry.yTicks.map((tick) => (
            <g key={`y-${tick.label}`}>
              <line
                x1={PAD_L}
                x2={VIEW_W - PAD_R}
                y1={tick.y}
                y2={tick.y}
                stroke="#d4d4d8"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <text x={PAD_L - 8} y={tick.y + 3} textAnchor="end" fontSize={10} fill="#71717a">
                {tick.label}
              </text>
            </g>
          ))}

          {geometry.xTicks.map((tick) => (
            <text
              key={`x-${tick.label}`}
              x={tick.x}
              y={VIEW_H - 8}
              textAnchor="middle"
              fontSize={10}
              fill="#71717a"
            >
              {tick.label}
            </text>
          ))}

          <polyline
            points={geometry.points}
            fill="none"
            stroke={HNT_COLOR}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  )
}
